import type { CatalogueDesign } from '../api/masters';
import { formatDate } from '../format/dates';
import { claimedOut, lookKey, pickGarment, sizeKey } from './catalogue';
import { claimedUnitIds, type BookingDraft } from './draft';
import { swapGarment } from './garmentSheet';
import type { AvailableUnit } from './rail';
import { garmentFromUnit, railFromCatalogue } from './takeBooking';

/**
 * A garment in the bag that another counter booked while this one was being taken — the booking
 * redesign's "taken meanwhile" recovery.
 *
 * <p>
 * <strong>Nothing is set aside until Confirm or Hold</strong>, so the garment chosen at 11:02 can
 * be booked at the next till at 11:04. The server is the guard and refuses the whole booking with a
 * conflict; what it cannot do is offer the way out. The rail is re-read from the server (on focus,
 * and after any refusal), and a garment it no longer calls free is named here with the next free
 * garment of the same design, look and size — one tap instead of a failed booking and a search.
 * </p>
 *
 * <p>
 * <strong>The server's own `free`, never the draft's view of it.</strong> A garment in the draft is
 * free as far as the server knows, because the draft holds nothing there; `claimedOut` would mark it
 * taken and every garment in the bag would read as lost.
 * </p>
 */
export interface TakenGarment {
  unitId: number;
  serialCode: string;
  /** The shop's own number, shown beside the serial (`withReference`). */
  referenceNumber?: string;
  /** The design's name, as the bag heads its row. */
  name: string;
  wearerName: string;
  /** "Back 24 Dec", or "Taken" when nothing says when. Never whose it is — the screen faces the customer. */
  back: string;
  /** The garment that would take its place, or null when nothing of that look and size is free. */
  replacement: AvailableUnit | null;
}

/**
 * Every garment in the draft the catalogue now says is not free. A garment absent from it is left
 * alone: the rail stops at its design cap, and absence proves nothing about being taken.
 *
 * <p>
 * Replacements never collide: each one chosen is counted as in the bag before the next is looked
 * for, so two lost 40s of one design are offered two different 40s, not the same one twice.
 * </p>
 */
export function takenMeanwhile(
  draft: BookingDraft,
  designs: readonly CatalogueDesign[],
): TakenGarment[] {
  const byId = new Map(
    designs.flatMap((design) =>
      design.units.map((unit) => [unit.unitId, { design, unit }] as const),
    ),
  );
  // The replacement goes on the booking as a rail garment, which is what a draft line is built from.
  const rail = new Map(railFromCatalogue(designs).map((unit) => [unit.unitId, unit]));
  const claimed = new Set(claimedUnitIds(draft));
  const taken: TakenGarment[] = [];

  for (const wearer of draft.wearers) {
    for (const garment of wearer.garments) {
      const found = byId.get(garment.unitId);

      if (!found || found.unit.free) {
        continue;
      }

      const { design, unit } = found;
      const chosen = pickGarment(claimedOut(design.units, claimed), {
        look: lookKey(unit),
        size: sizeKey(unit),
      });
      const replacement = chosen ? (rail.get(chosen.unitId) ?? null) : null;

      if (replacement) {
        claimed.add(replacement.unitId);
      }

      taken.push({
        unitId: garment.unitId,
        serialCode: garment.serialCode,
        ...(garment.referenceNumber ? { referenceNumber: garment.referenceNumber } : {}),
        name: garment.designName ?? garment.itemName,
        wearerName: wearer.name.trim() || 'Unnamed',
        back: unit.nextFreeFrom ? `Back ${formatDate(unit.nextFreeFrom)}` : 'Taken',
        replacement,
      });
    }
  }

  return taken;
}

/**
 * Puts each replacement in its garment's place — same person, same accessories, same discount,
 * since it is the same design (`swapGarment`). A garment with no replacement stays, still named, for
 * the clerk to take out or swap for another design.
 */
export function replaceTaken(
  draft: BookingDraft,
  taken: readonly TakenGarment[],
  days: number,
): BookingDraft {
  return taken.reduce(
    (next, garment) =>
      garment.replacement
        ? swapGarment(next, garment.unitId, garmentFromUnit(garment.replacement, days))
        : next,
    draft,
  );
}

/** The sentence over the list: "Booked at another counter meanwhile: 2 garments." */
export function takenHeadline(taken: readonly TakenGarment[]): string {
  return taken.length === 1
    ? `${taken[0]!.serialCode} was booked at another counter meanwhile`
    : `${taken.length} garments were booked at another counter meanwhile`;
}
